import type { CanvasItem, ChartOptions, ChartType, PaletteItem } from "@/types/pdfBuilder"

export type NewCanvasItem = Omit<CanvasItem, "id">

/** 팔레트 카드의 브라우저 기본(HTML5) 드래그가 dataTransfer 에 싣는 형식 — 캔버스가 이걸로 팔레트 드래그를 알아본다 */
export const PALETTE_DRAG_MIME = "application/x-fito-pdf-palette"

export const isPaletteDragEvent = (dataTransfer: DataTransfer | null) =>
  !!dataTransfer && Array.from(dataTransfer.types).includes(PALETTE_DRAG_MIME)

/**
 * 처음 추가될 때 리포트 템플릿 섹션의 지표를 가져오는 종류 — metrics 를 비워 두어야 Builder 컴포넌트가 시드한다.
 * []는 "사용자가 전부 해제"로 읽힌다 (#75)
 */
const SEEDED_TYPES: ChartType[] = [
  "summary",
  "table",
  "bar",
  "line",
  "combo",
  "activityGraph",
  "point",
  "sessionDetails",
  "scatter",
]

/** 그룹(같은 설정을 공유하는 아이템들) 안에서도 아이템마다 따로 가지는 옵션 */
const PER_ITEM_OPTION_FIELDS: Array<keyof ChartOptions> = [
  "title",
  "intervalView",
  "autoSize",
  "groupId",
]

export const isGroupSharedOptionField = (field: keyof ChartOptions) =>
  !PER_ITEM_OPTION_FIELDS.includes(field)

export const createGroupId = (prefix = "sd") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

/** Session Details 컨테이너의 기본 보기 — 처음 추가하면 세션·5분 차트가 보인다 (2026-09-30 결정) */
export const SESSION_DETAILS_DEFAULT_VIEWS = ["session", "5m"]

/**
 * Session Details 컨테이너 아이템 하나 — 세션·1분·5분·15분은 옵션(intervalViews)으로 켜고 끄며 한 아이템 안에 쌓인다.
 * Layout 이 Manual 이면 BuilderSessionDetails 가 보기마다 아이템(같은 groupId)으로 나눈다.
 */
export function buildSessionDetailsItem({
  x,
  y,
  w,
  h,
  minW,
  minH,
  dataSource,
  options,
}: {
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
  dataSource: CanvasItem["dataSource"]
  options?: Partial<ChartOptions>
}): NewCanvasItem {
  return {
    chartType: "sessionDetails" as const,
    x,
    y,
    w,
    h,
    minW,
    minH,
    dataSource: { ...dataSource },
    options: {
      title: "Session Details",
      ...(options ?? {}),
      intervalViews: [...SESSION_DETAILS_DEFAULT_VIEWS],
    },
  }
}

/**
 * 팔레트 항목 → 캔버스 아이템들. 팔레트 클릭(Add)과 캔버스 드롭이 같은 규칙을 쓴다.
 * Session Details 는 보기(세션·1분·5분·15분)를 안에 쌓는 컨테이너 하나다.
 */
export function buildItemsFromPalette(
  paletteItem: PaletteItem,
  x: number,
  y: number,
): NewCanvasItem[] {
  const dataSource: CanvasItem["dataSource"] = {
    type: "activity",
    activityIds: [],
    ...(SEEDED_TYPES.includes(paletteItem.type) ? {} : { metrics: [] }),
  }
  const base = {
    x,
    y,
    w: paletteItem.defaultWidth,
    h: paletteItem.defaultHeight,
    minW: paletteItem.minWidth,
    minH: paletteItem.minHeight,
  }

  if (paletteItem.type === "sessionDetails") {
    return [buildSessionDetailsItem({ ...base, dataSource, options: paletteItem.presetOptions })]
  }
  // Table 은 출력 장마다 아이템 하나가 되는 묶음 — 첫 아이템만 만들고 나머지 장은 BuilderDataTable 이 스스로 채운다 (2026-09-28)
  if (paletteItem.type === "table") {
    return [
      {
        chartType: paletteItem.type,
        ...base,
        dataSource,
        options: {
          title: paletteItem.label,
          ...(paletteItem.presetOptions ?? {}),
          groupId: createGroupId("tbl"),
        },
      },
    ]
  }

  return [
    {
      chartType: paletteItem.type,
      ...base,
      dataSource,
      options: { title: paletteItem.label, ...(paletteItem.presetOptions ?? {}) },
    },
  ]
}
